/**
 * RS / Card peek — second photo on finger contact, like the desktop hover.
 *
 * On touch the card behaves like the desktop hover: the moment the finger
 * touches the photo the second image appears — and it STAYS. It returns to
 * the first photo only when another card is touched or when the card leaves
 * the viewport (IntersectionObserver); lifting the finger changes nothing,
 * and touching anything that isn't a card leaves the photo as it is.
 *
 * Reuses the very previewImage/resetImage pair the desktop hover calls, so
 * variant-aware selection stays identical — the pair guards against touch
 * pointers, so it is invoked as a mouse. The second image is only
 * kick-loaded on the card's first contact, never upfront for the grid.
 *
 * One delegated document listener, passive, never preventDefault: vertical
 * scrolling stays native and the tap's click is never suppressed — a touch
 * still opens the product. Only native Horizon cards take part: the home
 * rails keep their single photo and their own drag physics.
 */

let active = null; // { target: product-card element, reset: () => void }

const release = () => {
  if (!active) return;
  observer.unobserve(active.target);
  active.reset();
  active = null;
};

// The photo returns to the first one when its card leaves the viewport
const observer = new IntersectionObserver((entries) => {
  for (const entry of entries) {
    if (!entry.isIntersecting && active?.target === entry.target) release();
  }
});

document.addEventListener(
  'pointerdown',
  (event) => {
    if (event.pointerType !== 'touch') return;
    if (!(event.target instanceof Element)) return;

    // Same media area the desktop hover listens on; the home rails never
    // contain a native card, and are excluded regardless
    const gallery = event.target.closest('.card-gallery');
    const card = gallery?.closest('product-card');
    if (!card || card.closest('[data-rs-rail]')) return;
    if (active?.target === card) return; // same card: the second photo stays
    if (gallery.querySelectorAll('slideshow-slide:not([hidden])').length < 2) return;
    if (typeof card.previewImage !== 'function') return;

    release();

    // First contact: kick-load the photo next to the current one (same trick
    // as the card's own hover preload)
    const slideshow = card.refs?.slideshow;
    const current = slideshow?.slides?.[slideshow.current] ?? gallery.querySelector('slideshow-slide[aria-hidden="false"]');
    current?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');

    card.previewImage({ pointerType: 'mouse' });
    active = { target: card, reset: () => card.resetImage?.({ pointerType: 'mouse' }) };
    observer.observe(card);
  },
  { capture: true, passive: true }
);
